// /guides — the app's guides: Markdown documents with ordered sections and images, versioned
// with the rest of the app's design. The old page's idiom: a collapsible left rail of
// guide rows grouped by the app each explains (hover actions + a ⋯ menu, a rail-footer "New
// guide"), and a main pane that previews the selected guide; here the preview also opens into an
// editor (sections in order, images, a live preview) and exports the guide to PDF.
import { Markdown, useConfirm } from '@cortex/ui';
import {
  AppWindow,
  ArrowDown,
  ArrowUp,
  Copy,
  FileDown,
  FileText,
  GraduationCap,
  History,
  ImagePlus,
  Loader2,
  MoreVertical,
  PanelLeftClose,
  PanelLeftOpen,
  Pencil,
  Play,
  Plus,
  RefreshCw,
  Save,
  Sparkles,
  Trash2,
  X,
} from 'lucide-react';
import * as React from 'react';
import { GuideHistory } from './GuideTour.tsx';
import { errorMessage, useGuidesClient, useGuidesHost } from './host.tsx';
import { launchTour, tourFromGuide } from './tour/launch.ts';
import {
  assetNameOf,
  draftOf,
  fileToBase64,
  groupGuides,
  move,
  pdfLabel,
  type DraftSection,
  type Guide,
  type GuideSummary,
} from './model.ts';

const btnGhost =
  'inline-flex items-center gap-1.5 rounded-md text-sm px-3 py-1.5 border border-card-border bg-transparent text-page-text cursor-pointer hover:bg-card-border/30 disabled:opacity-50 disabled:cursor-not-allowed';
const btnPrimary =
  'inline-flex items-center gap-1.5 rounded-md bg-nav-active text-white text-sm px-3 py-1.5 border-0 cursor-pointer disabled:opacity-50';
const input =
  'w-full rounded-md border border-input-border bg-card-bg text-page-text text-sm px-2.5 py-1.5 outline-none focus:border-nav-active';

const G = '/guides' as const;

async function unwrap<T>(p: Promise<{ data?: T; error?: unknown; response: Response }>): Promise<T> {
  let r;
  try {
    r = await p;
  } catch {
    throw new Error('The API is unreachable.');
  }
  if (r.error !== undefined || (r.data === undefined && r.response.status !== 204))
    throw new Error(errorMessage(r.error, r.response));
  return r.data as T;
}
const messageOf = (e: unknown) => (e instanceof Error ? e.message : String(e));

/** An asset image, fetched with the bearer (an <img src> cannot carry it) and cached per guide. */
function useAssetUrls(scope: string, guideKey: string | null) {
  const api = useGuidesClient();
  const [urls, setUrls] = React.useState<Record<string, string | 'error'>>({});
  const pending = React.useRef(new Set<string>());
  React.useEffect(() => {
    setUrls({});
    pending.current.clear();
  }, [guideKey, scope]);
  React.useEffect(
    () => () => {
      for (const u of Object.values(urls)) if (u !== 'error') URL.revokeObjectURL(u);
    },
    [urls],
  );
  const load = React.useCallback(
    (name: string, force = false) => {
      if (!guideKey || (!force && pending.current.has(name))) return;
      pending.current.add(name);
      void api
        .GET(`${G}/{key}/assets/{name}`, {
          params: { path: { key: guideKey, name } },
          parseAs: 'blob',
        })
        .then((r) => {
          const blob = r.data as Blob | undefined;
          setUrls((u) => ({ ...u, [name]: r.response.ok && blob ? URL.createObjectURL(blob) : 'error' }));
        })
        .catch(() => setUrls((u) => ({ ...u, [name]: 'error' })));
    },
    [guideKey, scope],
  );
  const reload = React.useCallback(
    (name: string) => {
      pending.current.delete(name);
      load(name, true);
    },
    [load],
  );
  return { urls, load, reload };
}

function AssetImage({
  name,
  alt,
  inline,
  known,
  urls,
  load,
}: {
  name: string;
  alt: string;
  inline: boolean;
  known: boolean;
  urls: Record<string, string | 'error'>;
  load: (name: string) => void;
}) {
  React.useEffect(() => {
    if (known) load(name);
  }, [known, name, load]);
  if (!known)
    return (
      <span
        className="inline-block text-xs text-amber-700 dark:text-amber-400"
        data-testid={`guide-missing-${name}`}
      >
        [missing image: {name}]
      </span>
    );
  const u = urls[name];
  if (!u) return <Loader2 size={14} className="inline animate-spin text-page-muted" />;
  if (u === 'error') return <span className="text-xs text-page-muted">[image unavailable: {name}]</span>;
  return inline ? (
    <img src={u} alt={alt || name} className="inline-block h-[1.4em] align-middle" />
  ) : (
    <img
      src={u}
      alt={alt || name}
      className="max-w-full rounded border border-card-border mx-auto"
      data-testid={`guide-img-${name}`}
    />
  );
}

function MenuItem({
  icon: Icon,
  label,
  onClick,
  danger,
}: {
  icon: typeof Plus;
  label: string;
  onClick: () => void;
  danger?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={
        'w-full text-left px-3 py-1.5 text-sm flex items-center gap-2 cursor-pointer border-0 bg-transparent hover:bg-card-border/30 ' +
        (danger ? 'text-red-600' : 'text-page-text')
      }
    >
      <Icon size={13} className="opacity-70" /> {label}
    </button>
  );
}

/** The Guides page of the app the host's client reaches (the companion's full page). */
export function GuidesPage() {
  const host = useGuidesHost();
  const api = useGuidesClient();
  const scope = host.scope;
  const place = React.useMemo(
    () => ({ scope, ...(host.routeOf ? { routeOf: host.routeOf } : {}) }),
    [scope, host.routeOf],
  );
  const BuildRequestButton = host.buildRequest?.Button;
  const [params, setParams] = host.useSearchParams();
  const { confirm } = useConfirm();
  const [guides, setGuides] = React.useState<GuideSummary[] | null>(null);
  const [canEdit, setCanEdit] = React.useState(false);
  const [err, setErr] = React.useState('');
  const [msg, setMsg] = React.useState('');
  const [loading, setLoading] = React.useState(true);
  const [menu, setMenu] = React.useState<string | null>(null);
  const [guide, setGuide] = React.useState<Guide | null>(null);
  const [editing, setEditing] = React.useState(false);
  const [busy, setBusy] = React.useState<
    '' | 'save' | 'export' | 'download' | 'upload' | 'delete' | 'create'
  >('');
  const [draft, setDraft] = React.useState<{
    title: string;
    description: string;
    app: string;
    sections: DraftSection[];
  } | null>(null);
  const [railOpen, setRailOpen] = React.useState(() => {
    try {
      return localStorage.getItem('guidesRailOpen') !== '0';
    } catch {
      return true;
    }
  });
  const [historyOf, setHistoryOf] = React.useState<{ key: string; title: string } | null>(null);
  const selectedKey = params.get('guide');
  const { urls, load: loadAsset, reload: reloadAsset } = useAssetUrls(scope, selectedKey);

  const toggleRail = () =>
    setRailOpen((o) => {
      try {
        localStorage.setItem('guidesRailOpen', o ? '0' : '1');
      } catch {
        /* storage off */
      }
      return !o;
    });

  const loadList = React.useCallback(async () => {
    setLoading(true);
    setErr('');
    try {
      const r = await unwrap(api.GET(G));
      setGuides(r.guides);
      setCanEdit(r.canEdit);
    } catch (e) {
      setErr(messageOf(e));
    } finally {
      setLoading(false);
    }
  }, [api]);

  React.useEffect(() => {
    setGuides(null);
    void loadList();
  }, [loadList]);

  const select = React.useCallback(
    (key: string | null) => {
      setEditing(false);
      setDraft(null);
      setMenu(null);
      const next = new URLSearchParams(params);
      if (key) next.set('guide', key);
      else next.delete('guide');
      setParams(next, { replace: true });
    },
    [params, setParams],
  );

  React.useEffect(() => {
    // A guide already in hand (just created) stays on screen while it is fetched fresh.
    setGuide((cur) => (cur && cur.key === selectedKey ? cur : null));
    if (!selectedKey) return;
    let alive = true;
    unwrap(api.GET(`${G}/{key}`, { params: { path: { key: selectedKey } } }))
      .then((g) => alive && setGuide(g))
      .catch((e) => alive && setErr(messageOf(e)));
    return () => {
      alive = false;
    };
  }, [selectedKey, api]);

  React.useEffect(() => {
    if (menu == null) return;
    const close = () => setMenu(null);
    window.addEventListener('click', close);
    return () => window.removeEventListener('click', close);
  }, [menu]);

  const startEdit = (g: Guide) => {
    setDraft({
      title: g.title,
      description: g.description ?? '',
      app: g.app ?? '',
      sections: g.sectionList.map(draftOf),
    });
    setEditing(true);
    setMsg('');
  };

  const create = async () => {
    setBusy('create');
    setErr('');
    try {
      const taken = new Set((guides ?? []).map((g) => g.title));
      let title = 'Untitled guide';
      for (let n = 2; taken.has(title); n++) title = `Untitled guide ${n}`;
      const g = await unwrap(
        api.POST(G, {
          body: {
            title,
            sections: [{ heading: 'Introduction', body: 'What this guide explains, and for whom.' }],
          },
        }),
      );
      await loadList();
      select(g.key);
      setGuide(g);
      startEdit(g);
    } catch (e) {
      setErr(messageOf(e));
    } finally {
      setBusy('');
    }
  };

  const save = async () => {
    if (!guide || !draft) return;
    setBusy('save');
    setErr('');
    try {
      const g = await unwrap(
        api.PUT(`${G}/{key}`, {
          params: { path: { key: guide.key } },
          body: {
            title: draft.title.trim() || guide.title,
            description: draft.description.trim() ? draft.description : null,
            app: draft.app.trim() ? draft.app.trim() : null,
            sections: draft.sections.map((s) => ({
              ...(s.id !== undefined ? { id: s.id } : {}),
              heading: s.heading.trim() || 'Untitled section',
              body: s.body,
            })),
          },
        }),
      );
      setGuide(g);
      setEditing(false);
      setDraft(null);
      setMsg('Saved.');
      await loadList();
    } catch (e) {
      setErr(messageOf(e));
    } finally {
      setBusy('');
    }
  };

  const remove = async (key: string, title: string) => {
    const ok = await confirm({
      title: 'Delete guide?',
      description: `"${title}" will be removed from this app for everyone. Its earlier versions stay in the app's history.`,
      confirmText: 'Delete',
      destructive: true,
    });
    if (!ok) return;
    setBusy('delete');
    try {
      await unwrap(api.DELETE(`${G}/{key}`, { params: { path: { key } } }));
      if (selectedKey === key) select(null);
      await loadList();
    } catch (e) {
      setErr(messageOf(e));
    } finally {
      setBusy('');
    }
  };

  const exportPdf = async () => {
    if (!guide) return;
    setBusy('export');
    setErr('');
    setMsg('');
    try {
      const e = await unwrap(api.POST(`${G}/{key}/export`, { params: { path: { key: guide.key } } }));
      setMsg(
        `PDF exported: ${e.pages} page${e.pages === 1 ? '' : 's'}${e.warnings.length ? ` — ${e.warnings.join('; ')}` : ''}.`,
      );
      const g = await unwrap(api.GET(`${G}/{key}`, { params: { path: { key: guide.key } } }));
      setGuide((cur) => (cur && editing ? { ...cur, exports: g.exports, latestExport: g.latestExport } : g));
      await loadList();
    } catch (e) {
      setErr(messageOf(e));
    } finally {
      setBusy('');
    }
  };

  // Launch tour: the shell's Spotlight takes it (src/tour/launch.ts, tour/TourHost.tsx).
  const launch = async (key: string) => {
    setErr('');
    try {
      const g =
        guide && guide.key === key
          ? guide
          : await unwrap(api.GET(`${G}/{key}`, { params: { path: { key } } }));
      if (!g.sectionList.length) {
        setMsg('This guide has no sections to walk through yet.');
        return;
      }
      if (selectedKey !== key) select(key);
      const t = tourFromGuide(g, place);
      if (!launchTour(t))
        setErr('The tour overlay is not loaded: reload the page and launch the tour again.');
    } catch (e) {
      setErr(messageOf(e));
    }
  };

  const downloadPdf = async (key: string) => {
    setBusy('download');
    setErr('');
    try {
      const r = await api.GET(`${G}/{key}/pdf`, {
        params: { path: { key }, query: {} },
        parseAs: 'blob',
      });
      if (!r.response.ok || !r.data) throw new Error(errorMessage(r.error, r.response));
      const url = URL.createObjectURL(r.data as Blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${key}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      setErr(messageOf(e));
    } finally {
      setBusy('');
    }
  };

  const upload = async (files: FileList | null) => {
    if (!guide || !files?.length) return;
    setBusy('upload');
    setErr('');
    try {
      const names: string[] = [];
      for (const f of Array.from(files)) {
        const name = assetNameOf(f.name);
        await unwrap(
          api.POST(`${G}/{key}/assets`, {
            params: { path: { key: guide.key } },
            body: { name, contentBase64: await fileToBase64(f) },
          }),
        );
        names.push(name);
        reloadAsset(name);
      }
      const g = await unwrap(api.GET(`${G}/{key}`, { params: { path: { key: guide.key } } }));
      setGuide((cur) =>
        cur ? { ...cur, assetList: g.assetList, assets: g.assets, missingAssets: g.missingAssets } : g,
      );
      setMsg(`Added ${names.join(', ')} — use ![description](asset:${names[0]}) in a section.`);
    } catch (e) {
      setErr(messageOf(e));
    } finally {
      setBusy('');
    }
  };

  const removeAsset = async (name: string) => {
    if (!guide) return;
    try {
      await unwrap(api.DELETE(`${G}/{key}/assets/{name}`, { params: { path: { key: guide.key, name } } }));
      setGuide((cur) => (cur ? { ...cur, assetList: cur.assetList.filter((a) => a.name !== name) } : cur));
    } catch (e) {
      setErr(messageOf(e));
    }
  };

  const assetNames = React.useMemo(() => new Set((guide?.assetList ?? []).map((a) => a.name)), [guide]);
  const renderAsset = React.useCallback(
    (name: string, alt: string, inline: boolean) => (
      <AssetImage
        name={name}
        alt={alt}
        inline={inline}
        known={assetNames.has(name)}
        urls={urls}
        load={loadAsset}
      />
    ),
    [assetNames, urls, loadAsset],
  );

  const setSection = (i: number, patch: Partial<DraftSection>) =>
    setDraft((d) =>
      d ? { ...d, sections: d.sections.map((s, j) => (j === i ? { ...s, ...patch } : s)) } : d,
    );

  const groups = groupGuides(guides ?? []);

  const Row = (g: GuideSummary) => {
    const selected = selectedKey === g.key;
    const open = menu === g.key;
    return (
      <div
        key={g.key}
        className={
          'group relative rounded-lg border transition-colors mb-1 ' +
          (selected
            ? 'border-nav-active bg-nav-active/5'
            : 'border-transparent hover:border-card-border hover:bg-card-border/20')
        }
      >
        <div className="flex items-center">
          <button
            type="button"
            data-testid={`guide-row-${g.key}`}
            onClick={() => select(g.key)}
            title="Preview this guide"
            className="flex-1 min-w-0 text-left px-3 py-2 flex items-center gap-2 cursor-pointer border-0 bg-transparent text-inherit"
          >
            <GraduationCap
              size={16}
              className={'shrink-0 ' + (selected ? 'text-nav-active' : 'text-page-muted')}
            />
            <span className="min-w-0 flex flex-col">
              <span className="text-sm text-page-text overflow-hidden text-ellipsis whitespace-nowrap">
                {g.title}
              </span>
              <span className="text-[11px] text-page-muted">
                {g.sections} section{g.sections === 1 ? '' : 's'}
                {g.latestExport ? ' · PDF' : ''}
              </span>
            </span>
          </button>
          {g.sections ? (
            <button
              type="button"
              title="Launch the tour"
              aria-label={`Launch the tour of ${g.title}`}
              data-testid={`guide-row-launch-${g.key}`}
              onClick={(e) => {
                e.stopPropagation();
                void launch(g.key);
              }}
              className="shrink-0 p-1 rounded cursor-pointer border-0 bg-transparent text-page-muted hover:text-nav-active opacity-0 group-hover:opacity-100 focus:opacity-100"
            >
              <Play size={14} />
            </button>
          ) : null}
          {g.latestExport ? (
            <button
              type="button"
              title="Download PDF"
              data-testid={`guide-row-pdf-${g.key}`}
              onClick={(e) => {
                e.stopPropagation();
                void downloadPdf(g.key);
              }}
              className="shrink-0 p-1 rounded cursor-pointer border-0 bg-transparent text-page-muted hover:text-nav-active opacity-0 group-hover:opacity-100"
            >
              <FileDown size={14} />
            </button>
          ) : null}
          {canEdit ? (
            <button
              type="button"
              data-testid={`guide-menu-${g.key}`}
              title="More"
              onClick={(e) => {
                e.stopPropagation();
                setMenu(open ? null : g.key);
              }}
              className={
                'shrink-0 mr-1 p-1 rounded cursor-pointer border-0 bg-transparent text-page-muted hover:text-page-text opacity-0 group-hover:opacity-100 ' +
                (open ? 'opacity-100' : '')
              }
            >
              <MoreVertical size={14} />
            </button>
          ) : null}
        </div>
        {open ? (
          <div
            className="absolute right-1 top-full z-40 mt-1 w-52 rounded-md border border-card-border bg-card-bg shadow-lg py-1 text-page-text"
            onClick={(e) => e.stopPropagation()}
          >
            {g.sections ? (
              <MenuItem icon={Play} label="Launch tour" onClick={() => (setMenu(null), void launch(g.key))} />
            ) : null}
            <MenuItem
              icon={History}
              label="Version history"
              onClick={() => (setMenu(null), setHistoryOf({ key: g.key, title: g.title }))}
            />
            <MenuItem
              icon={Pencil}
              label="Edit"
              onClick={() => {
                setMenu(null);
                select(g.key);
                setParams(new URLSearchParams({ guide: g.key, edit: '1' }), { replace: true });
              }}
            />
            {g.latestExport ? (
              <MenuItem
                icon={FileDown}
                label="Download PDF"
                onClick={() => (setMenu(null), void downloadPdf(g.key))}
              />
            ) : null}
            <MenuItem
              icon={Trash2}
              label="Delete guide"
              danger
              onClick={() => (setMenu(null), void remove(g.key, g.title))}
            />
          </div>
        ) : null}
      </div>
    );
  };

  // ?edit=1 opens the editor once the guide is loaded (the row menu's Edit).
  React.useEffect(() => {
    if (guide && params.get('edit') === '1' && !editing && canEdit) {
      startEdit(guide);
      const next = new URLSearchParams(params);
      next.delete('edit');
      setParams(next, { replace: true });
    }
  }, [guide, params, canEdit]);

  return (
    <div className="flex h-full min-h-0" data-tour="page-guides" data-testid="guides-page">
      {!railOpen ? (
        <div className="w-10 shrink-0 border-r border-card-border bg-card-bg/40 flex flex-col items-center py-3 gap-2">
          <button
            type="button"
            data-testid="guides-rail-open"
            onClick={toggleRail}
            title="Show guides list"
            className="text-page-muted hover:text-page-text bg-transparent border-0 cursor-pointer p-1"
          >
            <PanelLeftOpen size={18} />
          </button>
          <GraduationCap size={16} className="text-nav-active mt-1" />
        </div>
      ) : null}
      <aside
        className={
          (railOpen ? 'w-72' : 'hidden') +
          ' shrink-0 border-r border-card-border bg-card-bg/40 flex flex-col min-h-0'
        }
      >
        <div className="px-4 py-4 flex items-center gap-2 border-b border-card-border">
          <GraduationCap size={18} className="text-nav-active" />
          <span className="font-semibold text-page-text">Guides</span>
          <button
            type="button"
            data-testid="guides-refresh"
            onClick={() => void loadList()}
            title="Refresh"
            className="ml-auto text-page-muted hover:text-page-text bg-transparent border-0 cursor-pointer p-1"
          >
            {loading ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />}
          </button>
          <button
            type="button"
            data-testid="guides-rail-close"
            onClick={toggleRail}
            title="Collapse guides list"
            className="text-page-muted hover:text-page-text bg-transparent border-0 cursor-pointer p-1"
          >
            <PanelLeftClose size={16} />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto p-2">
          {loading && !guides ? (
            <div className="flex items-center gap-2 text-sm text-page-muted px-2 py-3">
              <Loader2 size={16} className="animate-spin" /> Loading guides…
            </div>
          ) : null}
          {guides && !guides.length ? (
            <div className="text-sm text-page-muted px-2 py-3" data-testid="guides-empty">
              No guides yet in this project.{canEdit ? ' Write one below.' : ''}
            </div>
          ) : null}
          {groups.map((grp) => (
            <div key={grp.app ?? '_project'} className="mb-2">
              <div className="px-2 pt-2 pb-1">
                <span className="text-[11px] font-semibold uppercase tracking-wide text-page-muted">
                  {grp.label}
                </span>
              </div>
              {grp.items.map(Row)}
            </div>
          ))}
        </div>
        <div className="p-2 border-t border-card-border grid gap-2">
          {canEdit ? (
            <button
              type="button"
              data-testid="guides-new"
              onClick={() => void create()}
              disabled={busy === 'create'}
              className="w-full inline-flex items-center justify-center gap-1.5 rounded-md text-sm px-3 py-2 border border-dashed border-card-border bg-transparent text-page-muted hover:text-page-text hover:bg-card-border/30 cursor-pointer"
            >
              {busy === 'create' ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />}{' '}
              New guide
            </button>
          ) : null}
          {BuildRequestButton && (
            <BuildRequestButton kind="new_guide" label="Build a guide" testId="guides-build-request" />
          )}
        </div>
      </aside>

      <main className="flex-1 min-w-0 min-h-0 relative bg-page-bg overflow-y-auto">
        {err ? (
          <div className="mx-6 mt-4 text-sm text-red-600" data-testid="guides-error">
            {err}
          </div>
        ) : null}
        {!selectedKey ? (
          <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-6">
            <GraduationCap size={40} className="text-page-muted mb-3" />
            <div className="text-page-text font-medium">Your project guides</div>
            <div className="text-sm text-page-muted mt-1 max-w-md">
              Guides are step-by-step documents through this project's apps and pages: sections in order, with
              screenshots. Pick one from the left to preview it, edit it, or export it as a PDF.
            </div>
            {canEdit ? (
              <button
                type="button"
                data-testid="guides-new-main"
                onClick={() => void create()}
                className={btnGhost + ' mt-4'}
              >
                <Sparkles size={14} /> New guide
              </button>
            ) : null}
          </div>
        ) : !guide ? (
          <div className="flex items-center gap-2 text-sm text-page-muted p-8">
            <Loader2 size={16} className="animate-spin" /> Loading…
          </div>
        ) : editing && draft ? (
          <div className="px-6 py-6" data-testid="guide-editor">
            <div className="flex items-center gap-2 mb-4">
              <Pencil size={18} className="text-nav-active" />
              <span className="font-semibold text-page-text">Editing</span>
              <span className="text-xs text-page-muted font-mono">{guide.key}</span>
              <div className="ml-auto flex items-center gap-2">
                <button
                  type="button"
                  className={btnGhost}
                  onClick={() => (setEditing(false), setDraft(null))}
                  disabled={busy === 'save'}
                >
                  <X size={14} /> Cancel
                </button>
                <button
                  type="button"
                  data-testid="guide-save"
                  className={btnPrimary}
                  onClick={() => void save()}
                  disabled={busy === 'save'}
                >
                  {busy === 'save' ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Save
                </button>
              </div>
            </div>
            <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
              <div className="space-y-3 min-w-0">
                <label className="block">
                  <span className="text-xs text-page-muted">Title</span>
                  <input
                    className={input}
                    data-testid="guide-title-input"
                    value={draft.title}
                    maxLength={300}
                    onChange={(e) => setDraft({ ...draft, title: e.target.value })}
                  />
                </label>
                <label className="block">
                  <span className="text-xs text-page-muted">Description</span>
                  <textarea
                    className={input + ' min-h-[56px]'}
                    data-testid="guide-description-input"
                    value={draft.description}
                    onChange={(e) => setDraft({ ...draft, description: e.target.value })}
                  />
                </label>
                <label className="block">
                  <span className="text-xs text-page-muted">Explains (app or page, optional)</span>
                  <input
                    className={input}
                    data-testid="guide-app-input"
                    value={draft.app}
                    maxLength={190}
                    onChange={(e) => setDraft({ ...draft, app: e.target.value })}
                  />
                </label>
                {draft.sections.map((s, i) => (
                  <div
                    key={s.uid}
                    className="rounded-lg border border-card-border bg-card-bg p-3"
                    data-testid={`guide-section-editor-${i}`}
                  >
                    <div className="flex items-center gap-2 mb-2">
                      <span className="shrink-0 w-6 h-6 rounded-full bg-nav-active/10 text-nav-active text-xs font-semibold flex items-center justify-center">
                        {i + 1}
                      </span>
                      <input
                        className={input}
                        data-testid={`guide-section-heading-${i}`}
                        value={s.heading}
                        placeholder="Section heading"
                        maxLength={300}
                        onChange={(e) => setSection(i, { heading: e.target.value })}
                      />
                      <button
                        type="button"
                        title="Move up"
                        className="p-1 bg-transparent border-0 cursor-pointer text-page-muted hover:text-page-text disabled:opacity-30"
                        disabled={i === 0}
                        onClick={() => setDraft({ ...draft, sections: move(draft.sections, i, -1) })}
                      >
                        <ArrowUp size={14} />
                      </button>
                      <button
                        type="button"
                        title="Move down"
                        className="p-1 bg-transparent border-0 cursor-pointer text-page-muted hover:text-page-text disabled:opacity-30"
                        disabled={i === draft.sections.length - 1}
                        onClick={() => setDraft({ ...draft, sections: move(draft.sections, i, 1) })}
                      >
                        <ArrowDown size={14} />
                      </button>
                      <button
                        type="button"
                        title="Remove section"
                        data-testid={`guide-section-remove-${i}`}
                        className="p-1 bg-transparent border-0 cursor-pointer text-page-muted hover:text-red-600"
                        onClick={() =>
                          setDraft({ ...draft, sections: draft.sections.filter((_, j) => j !== i) })
                        }
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                    <textarea
                      className={input + ' font-mono text-xs min-h-[140px]'}
                      data-testid={`guide-section-body-${i}`}
                      value={s.body}
                      placeholder="Markdown: **bold**, lists, tables, ![screenshot](asset:name.png)"
                      onChange={(e) => setSection(i, { body: e.target.value })}
                    />
                  </div>
                ))}
                <button
                  type="button"
                  data-testid="guide-section-add"
                  className={btnGhost}
                  onClick={() =>
                    setDraft({
                      ...draft,
                      sections: [
                        ...draft.sections,
                        draftOf({ heading: `Step ${draft.sections.length + 1}`, body: '' }),
                      ],
                    })
                  }
                >
                  <Plus size={14} /> Add section
                </button>
                <div
                  className="rounded-lg border border-card-border bg-card-bg p-3"
                  data-testid="guide-assets"
                >
                  <div className="flex items-center gap-2 mb-2">
                    <ImagePlus size={14} className="text-page-muted" />
                    <span className="text-sm font-medium text-page-text">Images</span>
                    <label className={btnGhost + ' ml-auto text-xs'}>
                      {busy === 'upload' ? (
                        <Loader2 size={12} className="animate-spin" />
                      ) : (
                        <ImagePlus size={12} />
                      )}{' '}
                      Add image
                      <input
                        type="file"
                        accept="image/png,image/jpeg,image/gif,image/webp,image/svg+xml"
                        multiple
                        className="hidden"
                        data-testid="guide-asset-input"
                        onChange={(e) => {
                          void upload(e.target.files);
                          e.target.value = '';
                        }}
                      />
                    </label>
                  </div>
                  {guide.assetList.length ? (
                    <ul className="space-y-1">
                      {guide.assetList.map((a) => (
                        <li key={a.name} className="flex items-center gap-2 text-xs">
                          <code className="font-mono text-page-text">asset:{a.name}</code>
                          <span className="text-page-muted">
                            {Math.max(1, Math.round(a.sizeBytes / 1024))} KB
                          </span>
                          <button
                            type="button"
                            title="Copy the Markdown for this image"
                            className="p-0.5 bg-transparent border-0 cursor-pointer text-page-muted hover:text-page-text"
                            onClick={() =>
                              void navigator.clipboard
                                ?.writeText(`![${a.name}](asset:${a.name})`)
                                .catch(() => {})
                            }
                          >
                            <Copy size={12} />
                          </button>
                          <button
                            type="button"
                            title="Remove image"
                            className="ml-auto p-0.5 bg-transparent border-0 cursor-pointer text-page-muted hover:text-red-600"
                            onClick={() => void removeAsset(a.name)}
                          >
                            <Trash2 size={12} />
                          </button>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <div className="text-xs text-page-muted">
                      No images yet. Added images are shown with ![description](asset:name.png).
                    </div>
                  )}
                </div>
              </div>
              <div
                className="min-w-0 rounded-lg border border-card-border bg-card-bg px-5 py-4"
                data-testid="guide-live-preview"
              >
                <div className="text-[11px] font-semibold uppercase tracking-wide text-page-muted mb-2">
                  Preview
                </div>
                <h1 className="text-xl font-semibold text-page-text m-0">
                  {draft.title || 'Untitled guide'}
                </h1>
                {draft.description ? (
                  <p className="text-sm text-page-muted mt-1">{draft.description}</p>
                ) : null}
                {draft.sections.map((s, i) => (
                  <section key={s.uid} className="mt-5">
                    <h2 className="text-base font-semibold text-page-text">
                      {i + 1}. {s.heading || 'Untitled section'}
                    </h2>
                    <Markdown source={s.body} asset={renderAsset} />
                  </section>
                ))}
              </div>
            </div>
          </div>
        ) : (
          <div className="max-w-3xl mx-auto px-6 py-8" data-testid="guide-preview">
            <div className="flex items-start gap-3 mb-1">
              <GraduationCap size={24} className="text-nav-active mt-0.5 shrink-0" />
              <div className="min-w-0">
                <h1 className="text-xl font-semibold text-page-text m-0" data-testid="guide-title">
                  {guide.title}
                </h1>
                {guide.description ? (
                  <p className="text-sm text-page-muted mt-1 mb-0">{guide.description}</p>
                ) : null}
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-2 mt-4 mb-6 pl-9">
              <button
                type="button"
                data-testid={`guide-launch-${guide.key}`}
                className={btnPrimary}
                disabled={!guide.sectionList.length}
                title={guide.sectionList.length ? 'Walk through the guide on the page' : 'No sections yet'}
                onClick={() => void launch(guide.key)}
              >
                <Play size={14} /> Launch tour
              </button>
              {canEdit ? (
                <button
                  type="button"
                  data-testid="guide-edit"
                  className={btnGhost}
                  onClick={() => startEdit(guide)}
                >
                  <Pencil size={14} /> Edit
                </button>
              ) : null}
              {canEdit ? (
                <button
                  type="button"
                  data-testid="guide-export"
                  className={btnGhost}
                  onClick={() => void exportPdf()}
                  disabled={busy === 'export'}
                >
                  {busy === 'export' ? (
                    <Loader2 size={14} className="animate-spin" />
                  ) : (
                    <FileText size={14} />
                  )}{' '}
                  Export PDF
                </button>
              ) : null}
              <button
                type="button"
                data-testid={`guide-pdf-${guide.key}`}
                data-pdf-state={guide.latestExport ? 'ready' : 'none'}
                className={btnGhost}
                disabled={!guide.latestExport || busy === 'download'}
                title={
                  guide.latestExport
                    ? `Download the newest PDF, ${pdfLabel(guide.latestExport)}`
                    : 'No PDF yet — export one first'
                }
                onClick={() => void downloadPdf(guide.key)}
              >
                {busy === 'download' ? (
                  <Loader2 size={14} className="animate-spin" />
                ) : (
                  <FileDown size={14} />
                )}{' '}
                Download PDF
                {guide.latestExport ? (
                  <span className="text-[11px] text-page-muted" data-testid={`guide-pdf-meta-${guide.key}`}>
                    {pdfLabel(guide.latestExport)}
                  </span>
                ) : null}
              </button>
              <button
                type="button"
                data-testid="guide-history-open"
                className={btnGhost}
                onClick={() => setHistoryOf({ key: guide.key, title: guide.title })}
              >
                <History size={14} /> Version history
              </button>
              {BuildRequestButton && (
                <BuildRequestButton
                  kind="change_guide"
                  target={guide.key}
                  label="Request a change"
                  heading={`Change the guide ${guide.title}`}
                  testId="guide-change-request"
                  look="ghost"
                />
              )}
              {canEdit ? (
                <button
                  type="button"
                  data-testid="guide-delete"
                  className={btnGhost + ' text-red-600'}
                  onClick={() => void remove(guide.key, guide.title)}
                >
                  <Trash2 size={14} /> Delete
                </button>
              ) : null}
              {guide.app && host.appPath ? (
                <a
                  href={host.appPath(guide.app)}
                  onClick={(e) => {
                    if (e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return;
                    e.preventDefault();
                    host.navigate(host.appPath!(guide.app!));
                  }}
                  title="Open the app this guide explains"
                  data-testid="guide-app-link"
                  className="inline-flex items-center gap-1 rounded-full border border-card-border text-xs text-page-text px-2.5 py-1 no-underline hover:border-nav-active"
                >
                  <AppWindow size={11} className="opacity-70" />{' '}
                  <span className="max-w-[180px] truncate">{guide.app}</span>
                </a>
              ) : null}
            </div>
            {msg ? (
              <div className="text-sm text-page-muted mb-3 pl-9" data-testid="guides-msg">
                {msg}
              </div>
            ) : null}
            {guide.missingAssets.length ? (
              <div className="text-xs text-amber-700 dark:text-amber-400 mb-3 pl-9">
                Missing images: {guide.missingAssets.join(', ')}
              </div>
            ) : null}
            <div className="rounded-lg border border-card-border bg-card-bg px-5 py-3">
              {!guide.sectionList.length ? (
                <div className="text-sm text-page-muted py-2">This guide has no sections yet.</div>
              ) : null}
              <ol className="list-none m-0 p-0">
                {guide.sectionList.map((s, i) => (
                  <li
                    key={s.id}
                    className="py-3 border-b border-card-border/60 last:border-b-0"
                    data-testid={`guide-section-${i}`}
                  >
                    <div className="flex items-start gap-3">
                      <span className="shrink-0 w-6 h-6 rounded-full bg-nav-active/10 text-nav-active text-xs font-semibold flex items-center justify-center mt-0.5">
                        {i + 1}
                      </span>
                      <div className="min-w-0 flex-1">
                        <div className="text-sm font-medium text-page-text">{s.heading}</div>
                        <Markdown source={s.body} asset={renderAsset} />
                      </div>
                    </div>
                  </li>
                ))}
              </ol>
            </div>
          </div>
        )}
      </main>
      {historyOf ? (
        <GuideHistory
          guide={historyOf}
          canEdit={canEdit}
          asset={renderAsset}
          onClose={() => setHistoryOf(null)}
          onRestored={(g, commit) => {
            setHistoryOf(null);
            setGuide(g);
            setMsg(`Restored the version of ${commit.slice(0, 8)} as a new commit.`);
            void loadList();
          }}
        />
      ) : null}
    </div>
  );
}
